import { signOut } from 'firebase/auth';
import createClient from 'openapi-fetch';
import { scheduleAccessEpochBump } from './access-epoch';
import { getActiveContext } from './active-context-store';
import { markSessionEnded } from './session-ended-notice';
import { auth } from './firebase';
import type { paths as CoreApiPaths } from './api-types';
import type { paths as FinancePaths } from './api-types-finance';
import type { paths as IdentityDocumentsPaths } from './api-types-identity-documents';
import type { paths as ExpensesPaths } from './api-types-expenses';
import type { paths as ExpenseReportsPaths } from './api-types-expenses-reports';
import type { paths as ExpenseWave2Paths } from './api-types-expenses-wave2';
import type { paths as ExpenseClosingPaths } from './api-types-expenses-closing';
import type { paths as ExpenseMileagePaths } from './api-types-expenses-mileage';
import type { paths as ExpensePurposeManagementPaths } from './api-types-expenses-purpose-management';
import type { paths as ExpenseFlatRatePaths } from './api-types-expenses-flat-rate';
import type { paths as ExpenseNotificationsPaths } from './api-types-expenses-notifications';
// #915: the nexpense parity routes finance serves; the PATCH extensions of the note and the expense
// ride on release 2's and release 1's routes, typed where they are sent.
import type { paths as ExpenseParityPaths } from './api-types-expenses-parity';
// #958: «Fornitori», from the contract draft (docs/specifications/expenses-suppliers.openapi.yaml).
// The search and the expense's PATCH stay on release 1's types until the picker takes them.
import type { paths as ExpenseSupplierPaths } from './api-types-expenses-suppliers';
import type { paths as ClinicalStudyPaths } from './api-types-clinical-study';
// The administration area's routes, from the contract DRAFT agreed with the finance session
// (docs/specifications/202610011100-console-administration-area.openapi.yaml), for what core-api
// does not serve yet. Whatever core-api already serves is typed by core-api's own generated paths,
// never by the draft — METHOD by method, because core-api lands a route's reads before its writes —
// and this import goes away when the last one lands.
import type { paths as AdministrationPaths } from './api-types-administration';

type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'options' | 'head' | 'patch' | 'trace';
/** The methods a generated path item actually serves (`post?: never` is not served). */
type Served<Item> = {
  [M in HttpMethod]: M extends keyof Item
    ? [NonNullable<Item[M]>] extends [never]
      ? never
      : M
    : never;
}[HttpMethod];
/** A draft path item with core-api's own operations laid over it, for the methods core-api serves. */
type Overlay<CoreItem, DraftItem> = Omit<DraftItem, Served<CoreItem>> &
  Pick<CoreItem, Served<CoreItem> & keyof CoreItem>;
type AdministrationOverlay = {
  [K in keyof AdministrationPaths]: K extends keyof CoreApiPaths
    ? Overlay<CoreApiPaths[K], AdministrationPaths[K]>
    : AdministrationPaths[K];
};

// `ExpensesPaths` (`./api-types-expenses`) is generated from the «Note spese» release-1 contract draft
// (#503) until the finance service serves `/v1/expenses/**`; it then comes from finance's own
// openapi.json, with the other finance routes.

// One typed client over the intersection of all three services' paths (SL-0025 R11, extended by
// SL-0041 R3) — not a third `createClient` instance. `core-api`, `identity-documents`, and
// `clinical-study` are all same-origin, routed purely by path prefix (SL-0041 R4: the Firebase
// Hosting rewrite / Vite dev-proxy route `/v1/clinical-study/**` to the clinical-study service and
// everything else under `/v1/**` to core-api), so one client safely carries all three path sets.
export const apiClient = createClient<
  Omit<CoreApiPaths, keyof AdministrationPaths> &
    FinancePaths &
    IdentityDocumentsPaths &
    // #958: release 1's supplier search gave way to the catalog's (expenses-suppliers contract).
    Omit<ExpensesPaths, keyof ExpenseMileagePaths | '/v1/expenses/suppliers'> &
    ExpenseMileagePaths &
    ExpenseReportsPaths &
    Pick<
      ExpenseWave2Paths,
      | '/v1/expenses/purposes'
      | '/v1/expenses/purposes/reserved'
      | '/v1/expenses/purposes/{code}/reserved'
      | '/v1/expenses/note-types'
      | '/v1/expenses/reports/{reportId}/pdf'
    > &
    ExpenseClosingPaths &
    ExpenseFlatRatePaths &
    ExpenseNotificationsPaths &
    Pick<
      ExpenseParityPaths,
      | '/v1/expenses/reports/{reportId}/documents'
      | '/v1/expenses/reports/{reportId}/documents/{documentId}'
      | '/v1/expenses/projects'
      | '/v1/expenses/clients'
      | '/v1/expenses/{expenseId}/class'
      // #915 F4: the Excel exports.
      | '/v1/expenses/exports/notes.xlsx'
      | '/v1/expenses/exports/expenses.xlsx'
    > &
    ExpensePurposeManagementPaths &
    Omit<ExpenseSupplierPaths, '/v1/expenses/{expenseId}'> &
    ClinicalStudyPaths &
    AdministrationOverlay
>({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
});

// FR-4.6/FR-6.3: no token, role, or permission is ever cached client-side. Every `/v1` request
// fetches a fresh bearer via the Firebase SDK's own getIdToken() cache/refresh — never a value
// held in React state — and reads the active-context SELECTION straight from the module-level
// store (never a value cached here). The server re-derives and enforces every permission per
// request; these headers only tell it which (Tenant, Organization) the caller has chosen.
//
// An admin call (SL-0023, Resolution 3) that already supplies its own X-Active-* headers via its
// own `params.header` is never overwritten by the ambient store below — this middleware only
// FILLS IN the ambient value when the caller has not already set one on the outgoing Request.
// clinical-study routes (SL-0041 R2) never carry a declared X-Active-* header requirement in
// their generated types, so every clinical-study call relies on this ambient fill-in exclusively.
let signingOut = false;

/** What the app declares about itself: shown in the session's history, never trusted (ADR-020). */
function displayMode(): 'standalone' | 'browser' {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone ? 'standalone' : 'browser';
}

async function problemCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.clone().json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * core-api answered 401 SESSION_ENDED: this sign-in was ended — here, elsewhere, or with «No, non
 * ero io» — and its refresh token can never be used again (ADR-020). Signing out of Firebase shows
 * the sign-in page, which says why. Several requests may fail together; one sign-out is enough.
 */
function endSignedOutSession(): void {
  markSessionEnded();
  if (signingOut) {
    return;
  }
  signingOut = true;
  void signOut(auth).finally(() => {
    signingOut = false;
  });
}

function isAdministrationWrite(url: string): boolean {
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/v1/administration/');
  } catch {
    return false;
  }
}

apiClient.use({
  async onRequest({ request }) {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    request.headers.set('X-Console-Display-Mode', displayMode());

    const activeContext = getActiveContext();
    if (activeContext) {
      if (!request.headers.has('X-Active-Tenant-Id')) {
        request.headers.set('X-Active-Tenant-Id', activeContext.tenantId);
      }
      if (!request.headers.has('X-Active-Organization-Id')) {
        request.headers.set('X-Active-Organization-Id', activeContext.organizationId);
      }
    }

    return request;
  },
  async onResponse({ request, response }) {
    if (response.status === 401 && (await problemCode(response)) === 'SESSION_ENDED') {
      endSignedOutSession();
    }
    // Decision 151: a write in the administration area may change what this caller may do (a module
    // activated, a role granted or revoked): the access reads run again, without waiting for the poll.
    if (response.ok && request.method !== 'GET' && isAdministrationWrite(request.url)) {
      scheduleAccessEpochBump();
    }
    return response;
  },
});
